/**
 * Set a timed flag that's persisted in local storage with a timeout value based on provided lifetime in minutes.
 * Setting value of 0 will expire the flag, and it will be removed with next `getTimedFlag`.
 *
 * @param {string} key
 * @param {number} lifetimeMinutes
 */
export function setTimedFlag(key, lifetimeMinutes) {
  const expirationTime = new Date();
  expirationTime.setMinutes(expirationTime.getMinutes() + lifetimeMinutes);
  localStorage.setItem(key, expirationTime.toISOString());
}

/**
 * Retrieve a timed flag set in local storage. Returns true if the flag is set and is not expired, false otherwise.
 *
 * @param {string} key
 * @return {boolean}
 */
export function getTimedFlag(key) {
  const expirationTime = localStorage.getItem(key);
  if (!expirationTime) {
    return false;
  }

  if (new Date() < new Date(expirationTime)) {
    return true;
  }

  // Expired or invalid localStorage item is removed
  localStorage.removeItem(key);
  return false;
}

/**
 * Set the timed flag via chosen URL query parameter. Parameter can be either empty or contain the flag lifetime
 * in minutes. Parameter is removed from the URL by invoking this method. Resulting flag value is returned.
 * Setting parameter to 0 will expire the previously set lifetime, but still set the flag true for current instance.
 *
 * This method was designed specifically for developer tools like debug and wip mode. Use with care.
 *
 * @param {string} urlParamName
 * @param {string} localStorageKey
 * @return {boolean}
 */
export function setTimedFlagFromUrlParam(urlParamName, localStorageKey) {
  const previousFlagValue = getTimedFlag(localStorageKey);

  const url = new URL(window.location);
  const queryValue = url.searchParams.get(urlParamName);

  if (queryValue === null) {
    return previousFlagValue;
  }

  const lifetimeMinutes = Number.parseInt(queryValue, 10);

  if (Number.isInteger(lifetimeMinutes)) {
    setTimedFlag(localStorageKey, lifetimeMinutes);
  }

  // Remove parameter from URL
  url.searchParams.delete(urlParamName);
  window.history.replaceState(window.history.state, '', url.href);

  return true;
}
